import React, { useEffect, useState } from "react";
import Button from "../Button";
import DropdownMenu from "../DropdownMenu";
import { Message, ThreadGroupAgregate } from "../../types";
import Icon from "../Icon";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import store from "../../store";

interface MessageThreadProps {
  thread_id?: string;
  name: string;
  resv_key: string;
  subtitle?: string;
  isSelected?: boolean;
  pinned?: boolean;
  muted?: boolean;
  archived?: boolean;

  last_message?: Message;
  source?: string;
  has_been_read?: boolean;

  onSelect?: (source?: string) => void;
  onPin?: (thread_id: string) => void;
  onArchive?: (thread_id: string) => void;
  onMute?: (thread_id: string) => void;
  skip_dropdown?: boolean;
  skip_has_been_read?: boolean;
  groups?: ThreadGroupAgregate;
  is_expanded?: boolean;
}

const truncateMessage = (message: Message, maxLength: number = 30): string => {
  if (!message.content) return "...";
  if (message.content.length <= maxLength) return message.content;
  return message.content.slice(0, maxLength) + "...";
};

const MessageThread: React.FC<MessageThreadProps> = ({
  thread_id,
  name,
  resv_key,
  subtitle,
  isSelected,
  pinned,
  archived,
  muted,
  last_message,
  onSelect,
  onPin,
  onArchive,
  source,
  onMute,
  has_been_read,
  skip_dropdown = false,
  skip_has_been_read = false,
  groups,
  is_expanded,
}) => {
  const [expanded, setExpanded] = useState(is_expanded);
  const { currentThreadSource } = useSelector(
    (state) => store.getState().messageStore
  );

  const formatTime = (dateString: Date) => {
    const date = new Date(dateString);
    const today = new Date();

    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const isToday = date >= today;
    const isYesterday = date >= yesterday && date < today;

    if (isToday) {
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? "PM" : "AM";
      const formattedHours = hours < 10 ? `0${hours}` : hours;
      const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
      return `${formattedHours}:${formattedMinutes}`;
    } else if (isYesterday) {
      return t("messaging.sentYesterday");
    } else {
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
  };
  const { t } = useTranslation();
  // if (groups) {
  //   console.log("values ", Object.values(groups));
  // }
  const groupList = Object.values(groups || {}).slice(1);
  const handleGroupClick = (source?: string) => {
    if (onSelect) {
      onSelect(source);
      if (groups?.active_groups && groups.active_groups > 1) {
        setExpanded(true);
      }
    }
  };
  useEffect(() => {
    // console.log("is expanded ", is_expanded);
    setExpanded(is_expanded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_expanded]);
  return (
    <ul
      className={`p-4 cursor-pointer m-2 relative
        ${isSelected ? "bg-cyan-100" : "hover:bg-gray-200"}
        ${
          has_been_read || skip_has_been_read
            ? "font-normal"
            : "bg-gray-200 font-bold"
        }
        ${last_message?.approval_status === 0 ? "animate-glow" : ""}
      `}
      onClick={() => {
        if (groups && groups?.active_groups > 1) {
          if (!isSelected) {
            setExpanded(!expanded);
          }
        } else if (onSelect) {
          onSelect(groups?.primary.source);
        }
      }}
    >
      {/* Unread Icon at Top-Left */}
      {/* {!has_been_read && !skip_has_been_read && (
        <div className="absolute bottom-1 right-1">
          <Icon name="unread" size="20px" />
        </div>
      )} */}

      <div className="flex items-center justify-between">
        {/* Icon Stack (Primary Above & Offset to the Right) */}

        <div
          className={`flex items-center relative w-12 h-10 justify-center mr-2 z-0
                    ${
                      groups?.active_groups !== undefined &&
                      groups?.active_groups === 2
                        ? "bg-gray-300 rounded-full pl-6 pr-1 py-1"
                        : groups?.active_groups === 3
                        ? "bg-gray-300 rounded-full pl-6 pr-1 py-6"
                        : ""
                    }`}
        >
          {skip_has_been_read && source && (
            <div className="absolute left-0 z-0">
              <Icon
                className={`bg-cyan-500 opacity-80`}
                name={source}
                size="25px"
              />
            </div>
          )}
          {!expanded && groups?.tertiary && groups.active_groups === 3 && (
            <div className="absolute right-2 top-6 z-0">
              <Icon
                className={`bg-cyan-500 opacity-80 ${
                  groups.active_groups > 1 ? "hover:opacity-30" : ""
                }`}
                name={
                  groups.tertiary.source ? groups.tertiary.source : "vikey_logo"
                }
                size="25px"
                onClick={() => {
                  if (groups.active_groups > 1) {
                    handleGroupClick(groups.tertiary.source);
                  }
                }}
              />
            </div>
          )}
          {!expanded && groups?.secondary && groups.active_groups > 1 && (
            <div
              className={`absolute left-0 ${
                groups.active_groups === 3 ? "bottom-3" : ""
              } z-0`}
            >
              <Icon
                className={`bg-cyan-500 opacity-80 ${
                  groups.active_groups > 1 ? "hover:opacity-30" : ""
                }`}
                name={
                  groups.secondary.source
                    ? groups.secondary.source
                    : "vikey_logo"
                }
                size="25px"
                onClick={() => {
                  if (groups.active_groups > 1) {
                    handleGroupClick(groups.secondary.source);
                  }
                }}
              />
            </div>
          )}
          {!expanded && groups?.primary && (
            <div
              className={`relative ${
                groups.active_groups === 3 ? "bottom-2" : ""
              } z-10`}
            >
              <Icon
                className={`bg-cyan-500 opacity-80 ${
                  groups.active_groups > 1 ? "hover:opacity-30" : ""
                }`}
                name={
                  groups.primary.source ? groups.primary.source : "vikey_logo"
                }
                onClick={() => {
                  if (groups.active_groups > 1) {
                    handleGroupClick(groups.primary.source);
                  }
                }}
                size="25px"
              />
            </div>
          )}
          {expanded && !isSelected && (
            <div className="pr-5">
              <Button
                color="info"
                icon="close"
                flat={true}
                onClick={() => {
                  if (!isSelected) {
                    setExpanded(!expanded);
                  }
                }}
                className="items-center"
              />
            </div>
          )}
        </div>

        <div className="flex flex-col flex-1">
          <span
            className={`text-sm font-semibold ${
              isSelected ? "text-cyan-600" : "text-gray-600"
            } `}
          >
            {name}
          </span>
          <span
            className={`text-sm font-semibold ${
              isSelected ? "text-cyan-600" : "text-gray-600"
            } `}
          >
            {subtitle ? subtitle : resv_key}
          </span>
          {last_message && (
            <span
              className={`text-sm ${
                isSelected ? "text-cyan-800" : "text-gray-400"
              }`}
            >
              {truncateMessage(last_message)}
            </span>
          )}
        </div>

        <div className="ml-auto text-right flex items-center">
          {muted && (
            <Icon className="bg-cyan-500 mr-2" name="silent" size="20px" />
          )}
          {pinned && (
            <Icon className="bg-cyan-500 mr-2" name="pin" size="20px" />
          )}
        </div>

        {/* Dropdown and date */}
        {!skip_dropdown && (
          <div className="relative ml-auto text-right overflow-visible">
            <DropdownMenu
              className="absolute right-0"
              triggerLabel=""
              // color="info"
              options={[
                {
                  label: pinned
                    ? t("messaging.actions.unpinChat")
                    : t("messaging.actions.pinChat"),
                  onClick: () => (onPin && thread_id ? onPin(thread_id) : ""),
                },
                {
                  label: archived
                    ? t("messaging.actions.unArchiveChat")
                    : t("messaging.actions.archiveChat"),
                  onClick: () =>
                    onArchive && thread_id ? onArchive(thread_id) : "",
                },
              ]}
            />
          </div>
        )}

        {last_message && !expanded && (
          <span
            className={`text-xs ${
              isSelected ? "text-cyan-600" : "text-gray-400"
            } block absolute top-2 right-2`}
          >
            {last_message.approval_status !== 0
              ? formatTime(new Date(last_message.sent_on))
              : t("messaging.assistant.messageNeedsApproval")}
          </span>
        )}
      </div>
      <div
        className={`transition-all duration-300 overflow-hidden
      ${expanded ? "max-h-[300px] opacity-100 mt-2" : "max-h-0 opacity-0"}
    `}
      >
        {groupList
          .filter(
            (group) =>
              group &&
              typeof group === "object" &&
              Object.keys(group).length > 0
          )
          .map((group: any, index: number) => (
            <>
              <div
                key={index}
                className={`mt-2 p-3 bg-grey-50 hover:bg-gray-100 ${
                  isSelected && currentThreadSource === group.source
                    ? "bg-cyan-200"
                    : "hover:bg-gray-200"
                }`}
                onClick={() => handleGroupClick(group.source)}
              >
                <div className="flex flex-col relative">
                  <div className="flex items-center mb-2">
                    <Icon
                      className="bg-cyan-500 mr-2"
                      name={group.source}
                      size="20px"
                    />
                    {group.source && group.source !== "manual" && (
                      <span className="text-sm">
                        {t("messaging.multipleSource." + group.source)}
                      </span>
                    )}
                  </div>

                  {group.last_message && (
                    <div className="mt-2 text-sm text-gray-600 relative">
                      {truncateMessage(group.last_message)}
                      <span className="text-xs text-gray-400 absolute bottom-10 right-0">
                        {group.last_message.approval_status !== 0
                          ? formatTime(new Date(group.last_message.sent_on))
                          : t("messaging.assistant.messageNeedsApproval")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </>
          ))}
      </div>
    </ul>
  );
};

export default React.memo(MessageThread);
